import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import RefreshIcon from '@material-ui/icons/Refresh'
import { useEffect } from 'react'
import { useCenarioMetadata } from 'src/modules/cenarios/hooks/useCenarioMetadata'
import { ErroCenarios } from '../hooks/useCenariosResolvidos'
import {
  CenarioResolvido,
  ResolucaoDetalhada,
  ResolucaoTipoDetalhada
} from '../types'

interface Props {
  aberto: boolean
  loading: boolean
  resolucao: ResolucaoDetalhada | null
  erro: ErroCenarios | null
  onToggle: (aberto: boolean) => void
  onReprocessar: () => void
}

export function CenariosResolvidosPanel({
  aberto,
  loading,
  resolucao,
  erro,
  onToggle,
  onReprocessar
}: Props) {
  const [{ metadata }, metadataActions] = useCenarioMetadata()

  useEffect(() => {
    metadataActions.fetchIfNeeded()
  }, [metadataActions])

  const rotuloTipo = (tipo: string) =>
    metadata?.tipos[tipo as keyof typeof metadata.tipos]?.label ?? tipo

  const rotuloChave = (tipo: string, chave: string) =>
    metadata?.tipos[tipo as keyof typeof metadata.tipos]?.chaves.find(
      c => c.chave === chave
    )?.label ?? chave

  const truncarDescricao = (descricao: string, tamanho = 40) =>
    descricao.length > tamanho
      ? `${descricao.slice(0, tamanho - 1)}…`
      : descricao

  const descreverVencedor = (tipo: string, vencedor: CenarioResolvido) =>
    `Cenário #${vencedor.codigo} · ${truncarDescricao(vencedor.descricao)}` +
    Object.entries(vencedor.valores)
      .map(([chave, valor]) => ` · ${rotuloChave(tipo, chave)}: ${valor}`)
      .join('')

  const linhas = (itens: ResolucaoTipoDetalhada[], prefixo?: string) =>
    itens.map(item => {
      const desempates = item.candidatos.filter(
        c => c.status === 'perdeu-desempate'
      )

      return (
        <TableRow key={`${prefixo ?? 'cabecalho'}-${item.tipo}`}>
          <TableCell>{rotuloTipo(item.tipo)}</TableCell>
          <TableCell>
            {item.vencedor
              ? descreverVencedor(item.tipo, item.vencedor)
              : 'Nenhum cenário aplicado'}
            {desempates.map(c => (
              <Typography
                key={c.cenarioId}
                variant="caption"
                color="textSecondary"
                display="block"
              >
                Cenário #{c.codigo} · {truncarDescricao(c.descricao)} perdeu no
                desempate
                {c.motivo ? `: ${c.motivo}` : ''}
              </Typography>
            ))}
          </TableCell>
        </TableRow>
      )
    })

  const conflito = erro?.status === 409

  const todosItens = resolucao
    ? [...resolucao.cabecalho, ...resolucao.porPerna.flat()]
    : []
  const totalAplicados = todosItens.filter(item => item.vencedor).length

  const resumo = loading
    ? 'Carregando...'
    : erro
    ? conflito
      ? 'Conflito no cadastro de cenários'
      : 'Não foi possível carregar'
    : resolucao
    ? `${totalAplicados}/${todosItens.length} tipos com cenário aplicado`
    : ''

  return (
    <Box mt={2}>
      <Accordion
        expanded={aberto}
        onChange={(_, expandido) => onToggle(expandido)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            mr={1}
          >
            <Typography variant="subtitle1">
              Cenários aplicados a este lote
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography
                variant="body2"
                color={erro ? 'error' : 'textSecondary'}
                style={{ marginRight: 8 }}
              >
                {resumo}
              </Typography>
              <Button
                type="button"
                size="small"
                startIcon={<RefreshIcon />}
                disabled={loading}
                onClick={event => {
                  event.stopPropagation()
                  onReprocessar()
                }}
              >
                Reprocessar
              </Button>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box width="100%">
            {erro && (
              <Box mb={2}>
                <Typography variant="body2" color="error">
                  {erro.message}
                </Typography>
                {conflito && (
                  <Typography variant="body2" color="textSecondary">
                    Corrija o cadastro em Cenários (não no lote) e reprocesse.
                  </Typography>
                )}
              </Box>
            )}

            {resolucao && !loading && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Resultado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhas(resolucao.cabecalho)}
                  {resolucao.porPerna.map((itens, index) =>
                    linhas(itens, `perna-${index}`)
                  )}
                </TableBody>
              </Table>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
